import moment from 'moment-timezone';
import { call, put, select } from 'redux-saga/effects';

import { eventClient } from 'clients';
import { makeSelectSamplePointById } from 'store/modules/samplePoints/selectors';
import { makeSelectTriggersBySamplePointId } from 'store/modules/triggers/selectors';
import { MFBEvent } from 'types/models/event';
import { HeaterPowerIssueEvent, SamplePointWithHeaterAddOn } from 'types/models/heater';
import { SamplePointId } from 'types/models/samplePoint';
import Trigger from 'types/models/trigger';
import { APIResponseParsed } from 'types/response';
import { TriggerMessage } from 'types/trigger.enum';

import {
  loadHeaterPowerIssueEvents,
  loadHeaterPowerIssueEventsFailure,
  loadHeaterPowerIssueEventsSuccess,
  setHeaterPowerIssueEvents
} from '../actions';

/**
 * Transforms event in API response to HeaterPowerIssueEvent format
 */
function transformToHeaterPowerIssueEvent(event: MFBEvent): HeaterPowerIssueEvent | null {
  const validMessages = [
    TriggerMessage.HEATER_POWER_SUPPLY_ISSUE,
    TriggerMessage.HEATER_POWER_SUPPLY_RECOVERED
  ];

  if (!validMessages.includes(event.message)) {
    return null;
  }

  return {
    date: event.date,
    message: event.message as HeaterPowerIssueEvent['message']
  };
}

export function* requestHeaterPowerIssueEvents(
  action: ReturnType<typeof loadHeaterPowerIssueEvents>
) {
  const { hostSamplePointId, startDateMs, endDateMs } = action.payload;

  // Get the host sample point to access the voltage sample point ID
  const hostSamplePoint: SamplePointWithHeaterAddOn | undefined = yield select(
    makeSelectSamplePointById(hostSamplePointId)
  );

  const voltageSamplePointId: SamplePointId | undefined = hostSamplePoint?._hidden?.heater?.supplyVoltage;

  if (!hostSamplePoint || !voltageSamplePointId) {
    // No voltage sample point, no events to fetch
    yield put(setHeaterPowerIssueEvents([]));
    yield put(loadHeaterPowerIssueEventsSuccess());
    return;
  }

  const powerSupplyAlertTriggers: Trigger[] = yield select(
    makeSelectTriggersBySamplePointId(voltageSamplePointId)
  );

  const triggerIds = powerSupplyAlertTriggers.map(t => t.id);

  if (triggerIds.length === 0) {
    // No triggers configured, no events to fetch
    yield put(setHeaterPowerIssueEvents([]));
    yield put(loadHeaterPowerIssueEventsSuccess());
    return;
  }

  const timezoneCode = hostSamplePoint.siteTimezoneCode;
  const startDateIso = timezoneCode ? moment(startDateMs).tz(timezoneCode).format() : '';
  const endDateIso = timezoneCode ? moment(endDateMs).tz(timezoneCode).format() : '';

  const response: APIResponseParsed<MFBEvent[]> = yield call(
    eventClient.fetchEventsByTriggers,
    triggerIds,
    { startDateIso, endDateIso }
  );

  if (response.data) {
    // Transform events to HeaterPowerIssueEvent format and filter out nulls
    const heaterPowerIssueEvents: HeaterPowerIssueEvent[] = response.data
      .map(transformToHeaterPowerIssueEvent)
      .filter((event): event is HeaterPowerIssueEvent => event !== null)
      .sort((a, b) => a.date - b.date); // Sort chronologically

    yield put(setHeaterPowerIssueEvents(heaterPowerIssueEvents));
    yield put(loadHeaterPowerIssueEventsSuccess());
  } else if (response.error) {
    const message = response.error.message || 'Failed to load heater power issue events';
    yield put(loadHeaterPowerIssueEventsFailure(message));
  }
}
